import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Award, Calendar, Search, TrendingUp } from 'lucide-react';
import { CertificateQuickActions } from './CertificateQuickActions';
import { format } from 'date-fns';
import { CertificateData } from '@/components/course/certificate/CertificateService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CertificateGalleryProps {
  certificates: CertificateData[];
  onViewCertificate?: (certificate: CertificateData) => void;
}

export const CertificateGallery: React.FC<CertificateGalleryProps> = ({ 
  certificates, 
  onViewCertificate 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'score' | 'name'>('date');

  // Filter certificates based on search
  const filteredCertificates = certificates.filter(cert =>
    cert.courseTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cert.certificateNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort certificates
  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.completionDate).getTime() - new Date(a.completionDate).getTime();
      case 'score':
        return b.quizAverage - a.quizAverage;
      case 'name':
        return a.courseTitle.localeCompare(b.courseTitle);
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search certificates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Sort by Date</SelectItem>
            <SelectItem value="score">Sort by Score</SelectItem>
            <SelectItem value="name">Sort by Name</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Certificate Grid */}
      {sortedCertificates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Award className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              {searchQuery ? 'No certificates match your search' : 'No certificates earned yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedCertificates.map((cert) => {
            const isNew = new Date(cert.completionDate).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;

            return (
              <Card 
                key={cert.id} 
                className="group hover:shadow-lg transition-shadow duration-200 border-border hover:border-primary/50"
              >
                <CardContent className="p-6 space-y-4">
                  {/* Certificate Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Award className="h-5 w-5 text-primary shrink-0" />
                        {isNew && (
                          <Badge variant="default" className="text-xs">
                            New
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors">
                        {cert.courseTitle}
                      </h3>
                    </div>
                  </div>

                  {/* Certificate Details */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">
                        {format(new Date(cert.completionDate), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    
                    <div className="flex items-center text-muted-foreground">
                      <TrendingUp className="h-4 w-4 mr-2 shrink-0" />
                      <span>Score: {Math.round(cert.quizAverage)}%</span>
                      {cert.quizAverage >= 90 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Excellence
                        </Badge>
                      )}
                    </div>

                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-1">Certificate Number</p>
                      <p className="text-xs font-mono break-all">#{cert.certificateNumber}</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="pt-2">
                    <CertificateQuickActions
                      certificateId={cert.id}
                      certificateNumber={cert.certificateNumber}
                      verificationHash={cert.verificationHash}
                      onView={onViewCertificate ? () => onViewCertificate(cert) : undefined}
                      variant="card"
                      showQR={false}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
